import React, { useState, useEffect } from "react";
import { COLORS } from "../config/themes";
import { Button, Badge } from "../components";
import { fetchAbsenceRequests, cancelAbsenceRequest } from "../utils/db";
import TeacherAbsenceForm from "../features/TeacherAbsenceForm";

const formatDate = (dateStr) => {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

const todayStr = () => new Date().toISOString().split("T")[0];

const REASON_LABELS = {
  sick: "Sick / Illness", personal: "Personal Day", family: "Family Emergency",
  medical: "Medical Appointment", bereavement: "Bereavement", other: "Other",
};

const MyAttendancePage = ({ currentUser, schoolId, teacherAttendance, setTeacherAttendance }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [cancelling, setCancelling] = useState(null);

  const loadRequests = async () => {
    setLoading(true);
    const all = await fetchAbsenceRequests(schoolId);
    setRequests(all.filter(r => r.teacherId === currentUser.id));
    setLoading(false);
  };

  useEffect(() => { loadRequests(); }, [schoolId, currentUser.id]);

  const handleCancel = async (req) => {
    setCancelling(req.id);
    await cancelAbsenceRequest(req.id);
    // Remove attendance entries for future dates only
    const newAtt = { ...teacherAttendance };
    const today = todayStr();
    req.dates.filter(d => d >= today).forEach(date => {
      if (newAtt[date]?.[currentUser.id]) {
        delete newAtt[date][currentUser.id];
      }
    });
    setTeacherAttendance(newAtt);
    await loadRequests();
    setCancelling(null);
  };

  const upcoming = requests.filter(r => r.status === "active" && r.dates.some(d => d >= todayStr()));
  const past = requests.filter(r => r.status === "cancelled" || r.dates.every(d => d < todayStr()));

  const RequestCard = ({ req }) => {
    const isCancelled = req.status === "cancelled";
    const isPast = req.dates.every(d => d < todayStr());
    const canCancel = !isCancelled && req.dates.some(d => d >= todayStr());
    const dateLabel = req.dates.length === 1
      ? formatDate(req.dates[0])
      : `${formatDate(req.dates[0])} → ${formatDate(req.dates[req.dates.length - 1])} (${req.dates.length} days)`;

    return (
      <div style={{ background: COLORS.card, borderRadius: 16, padding: "20px 24px", border: `1px solid ${isCancelled ? COLORS.borderLight : COLORS.border}`, opacity: isCancelled ? 0.6 : 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 15, color: COLORS.navy }}>
              {req.type === "late" ? "Late Arrival" : "Absence"}
            </span>
            <span style={{ padding: "2px 10px", borderRadius: 100, fontSize: 11, fontWeight: 700, background: isCancelled ? COLORS.borderLight : req.type === "late" ? COLORS.warningPale : COLORS.dangerPale, color: isCancelled ? COLORS.textMuted : req.type === "late" ? COLORS.warning : COLORS.danger }}>
              {isCancelled ? "Cancelled" : isPast ? "Past" : "Upcoming"}
            </span>
          </div>
          <div style={{ fontSize: 14, color: COLORS.text, marginBottom: 4 }}><Icon name="events" size={14} /> {dateLabel}</div>
          {req.type === "late" && req.estimatedArrival && (
            <div style={{ fontSize: 13, color: COLORS.textMuted }}><Icon name="clock" size={13} /> Estimated arrival: {req.estimatedArrival}</div>
          )}
          {req.type === "out" && req.reason && (
            <div style={{ fontSize: 13, color: COLORS.textMuted }}>Reason: {REASON_LABELS[req.reason] || req.reason}</div>
          )}
          {req.note && (
            <div style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 4, fontStyle: "italic" }}>"{req.note}"</div>
          )}
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 8 }}>
            Submitted {new Date(req.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </div>
        </div>
        {canCancel && (
          <Button variant="secondary" onClick={() => handleCancel(req)} disabled={cancelling === req.id} style={{ flexShrink: 0 }}>
            {cancelling === req.id ? "Cancelling..." : "Cancel"}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 28, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}>My Attendance</h2>
          <p style={{ margin: "4px 0 0", color: COLORS.textMuted, fontSize: 14 }}>Report absences and late arrivals</p>
        </div>
        <Button onClick={() => setShowForm(true)}>+ Report Absence / Late</Button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: COLORS.textMuted }}>Loading...</div>
      ) : (
        <>
          {/* Upcoming */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 12 }}>
              Upcoming ({upcoming.length})
            </div>
            {upcoming.length === 0 ? (
              <div style={{ background: COLORS.card, borderRadius: 16, padding: "28px 24px", border: `1px solid ${COLORS.border}`, textAlign: "center", color: COLORS.textMuted, fontSize: 14 }}>
                No upcoming absences or late arrivals reported.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {upcoming.map(r => <RequestCard key={r.id} req={r} />)}
              </div>
            )}
          </div>

          {/* Past / Cancelled */}
          {past.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 12 }}>
                Past & Cancelled ({past.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {past.slice(0, 10).map(r => <RequestCard key={r.id} req={r} />)}
              </div>
            </div>
          )}
        </>
      )}

      <TeacherAbsenceForm
        open={showForm}
        onClose={() => setShowForm(false)}
        currentUser={currentUser}
        schoolId={schoolId}
        teacherAttendance={teacherAttendance}
        setTeacherAttendance={setTeacherAttendance}
        onSubmitted={loadRequests}
      />
    </div>
  );
};

export default MyAttendancePage;
